import * as React from 'react'
import { LinkProps } from 'next/link'

import type {
  Block,
  ComponentBlock,
  DocumentBlock,
  InstanceBlock,
  ReferenceBlock,
} from '@basehub/data/replicache/index'

import { useTabsStore } from '../../../tabs/store'
import { $label } from '~/sotano-kit/primitives/input'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { BlockIcon } from '~/components/block-icon'
import { clsx } from 'clsx'
import { useDroppable } from '@dnd-kit/core'
import { editorDndControllers, isValidDnd } from '../dnd'
import {
  isComponentBlock,
  isInstanceBlock,
  isReferenceBlock,
  isRichTextBlock,
  normalizeStringPath,
} from '@basehub/data/replicache/index'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { getBlockDisplayData } from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons'
import { Blocks } from '../..'
import { useStorage } from '~/hooks/use-storage'
import { BlockPreview } from '~/components/block-preview'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { useRepoStore } from '../../../repo-store'
import { useEditorStore } from '../../../editor-store'
import { useBlockWrapperStore } from '../../block-wrapper'
import { useQueryClient } from '@tanstack/react-query'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { AtLeast } from '@basehub/utils/tree/index'
import { useHasRendered } from '~/hooks/use-has-rendered'
import { AlertIcon } from '~/sotano-kit/icons/alert'

type NestedDocumentBlockLinkPropsWithActions = {
  mode: 'withActions'
  path: string
  allowOptions?: boolean
  hidePreview?: boolean
} & Pick<
  React.HTMLAttributes<HTMLDivElement>,
  'onFocus' | 'draggable' | 'onDragStart' | 'onDragEnd' | 'onClick'
>

type NestedDocumentBlockLinkPropsReadonly = {
  mode: 'readonly'
}

type NestedDocumentBlockLinkPropsBase = {
  block: DocumentBlock | ComponentBlock | InstanceBlock
  parentBlock: Block | null
  wrapperClassName?: string
  className?: string
  children?: React.ReactNode
  disabled?: boolean
  hidePreview?: boolean
  prefix?: string
}
type NestedDocumentBlockLinkProps = NestedDocumentBlockLinkPropsBase &
  (
    | NestedDocumentBlockLinkPropsWithActions
    | NestedDocumentBlockLinkPropsReadonly
  )

export const NestedDocumentBlockLink: React.FC<NestedDocumentBlockLinkProps> = (
  props
) => {
  if (props.mode === 'readonly') {
    const { wrapperClassName, ...rest } = props
    return (
      <div
        key={props.block.id}
        className={$label({
          class: clsx(
            '!mx-0 border-dark-border-solid !px-0 pointer-events-none group/link-wrapper relative flex h-[39px] bg-dark-base select-none items-center justify-between !rounded-md border !py-0 !pl-0 text-sm font-medium focus-within:border-dark-control-base focus:border-dark-control-base focus:outline-none',
            wrapperClassName
          ),
        })}
      >
        <NestedDocumentBlockLinkVanilla {...rest} expanded={false} />
      </div>
    )
  }

  return <NestedDocumentBlockLinkWithActions {...props} />
}

const NestedDocumentBlockLinkWithActions: React.FC<
  NestedDocumentBlockLinkPropsBase &
    Omit<NestedDocumentBlockLinkPropsWithActions, 'mode'>
> = ({
  block,
  parentBlock,
  allowOptions = true,
  className,
  wrapperClassName,
  children,
  hidePreview,
  path,
  ...props
}) => {
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const getChildrenKeys = useWorkingTreeStore((s) => s.tree?.getChildrenKeys)
  const { activeTabKey: blockId } = useDashboardSlugs()
  const hasRendered = useHasRendered()
  const childrenKeys = React.useMemo(() => {
    try {
      return getChildrenKeys?.(block.id) ?? []
    } catch (err) {
      return []
    }
  }, [block.id, getChildrenKeys])

  const droppable = useDroppable({
    id: `component-bottom/${block.id}`,
    disabled: !hasRendered,
    data: editorDndControllers.droppableDataSchema.parse({
      newParentKey: block.id,
      intendedIndex: childrenKeys.length,
      type: editorDndControllers.dndType,
    }),
  })

  const isValid = React.useMemo(() => {
    if (
      !droppable.active ||
      !droppable.isOver ||
      !droppable.over ||
      isInstanceBlock(block)
    ) {
      return false
    }

    const parsed = editorDndControllers.draggableDataSchema.safeParse(
      droppable.active.data.current
    )
    if (!parsed.success) return

    const activeItemData = parsed.data

    return isValidDnd({
      keysDragged: activeItemData.keysDragged,
      newParentKey: block.id,
      // Plus one so that it takes it to the end
      intendedIndex: childrenKeys.length + 1,
      dragItemIndex: activeItemData.index,
      allowDragToSamePosition: true,
      draggedType: parsed.data.type,
      allowedType: 'editor-dnd',
      currentParentId: parsed.data.currentParentKey,
    })
  }, [block, childrenKeys, droppable.active, droppable.isOver, droppable.over])

  const setFocus = useRepoStore((s) => s.setFocus)
  const addTab = useTabsStore((s) => s.addTab)

  let targetBlock: Block | undefined
  let targetBlockParent: Block | undefined
  let isTargetUnresolved = false
  if (isInstanceBlock(block) && getNode) {
    const targetBlockNode = getNode(block.typeOptions.targetBlockId, {
      fallbackKey: 'sourceBlockId',
    })
    targetBlock = targetBlockNode?.value
    targetBlockParent = targetBlockNode?.parentKey
      ? getNode(targetBlockNode.parentKey, {
          fallbackKey: 'sourceBlockId',
        })?.value
      : undefined
    isTargetUnresolved = !targetBlock
  }

  const activeDocument = blockId
    ? getNode?.(blockId, { fallbackKey: 'sourceBlockId' })?.value
    : null

  const pathForNestedBlocks = normalizeStringPath({
    path,
    edgeId: block.id,
    format: 'string',
    includeEdge: true,
  })

  const { expanded, setExpanded } = useExpandedState(block)

  const isSelected = useEditorStore(
    (s) => s.selectedBlockId === idToSourceBlockId(block)
  )
  const isRenamingBlock = useEditorStore(
    (s) => s.isRenamingBlockId === idToSourceBlockId(block)
  )
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)

  React.useEffect(() => {
    if (!isSelected) return

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowRight' && expanded === false) {
        setExpanded(true)
      }
      if (e.key === 'ArrowLeft' && expanded === true) {
        setExpanded(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [expanded, isSelected, setExpanded])

  const targetTitle =
    targetBlock && targetBlockParent
      ? getBlockDisplayData({
          block: targetBlock,
          parentBlock: targetBlockParent,
        }).displayTitle
      : ''

  if (!getNode) return null
  const node = (
    <div
      ref={droppable.setNodeRef}
      key={block.id}
      className={clsx(
        isValid ? 'border-dark-control-base' : 'border-dark-border-solid',
        '-mx-2 rounded-md group/link-wrapper flex flex-col items-start relative select-none bg-dark-base border has-[>a:focus-within]:!border-dark-elevated has-[>a:focus]:!border-dark-control-base focus:outline-none',
        wrapperClassName
      )}
      onContextMenu={!allowOptions ? (e) => e.stopPropagation() : undefined}
      {...props}
    >
      <NestedDocumentBlockLinkVanilla
        block={block}
        parentBlock={parentBlock}
        className={clsx(
          className,
          'h-[39px] min-h-[39px] w-full relative text-sm font-medium',
          expanded ? 'rounded-[5px] rounded-b-none' : 'rounded-t-[5px]'
        )}
        onFocus={() => setSelectedBlockId(null)}
        disabled={props.disabled}
        expanded={expanded}
      >
        <div className="flex items-center justify-between w-full min-w-0">
          <div className="overflow-hidden text-ellipsis whitespace-nowrap">
            {isRenamingBlock && parentBlock ? (
              <>
                <RenamingInput block={block} path={path} parent={parentBlock} />
              </>
            ) : (
              <>{children || <span>{block.title || 'Untitled'}</span>}</>
            )}
          </div>

          <div className="flex items-center gap-0.5 pr-2 shrink-0">
            {targetBlock && isComponentBlock(targetBlock) && (
              <span
                className="text-xs text-dark-muted hover:text-dark-dim pl-1.5 pr-2 py-0.5 hover:bg-dark-border-solid flex items-center space-x-1 rounded"
                onClick={(e) => {
                  if (!targetBlock) return
                  e.stopPropagation()
                  e.preventDefault()
                  addTab({
                    key: idToSourceBlockId(targetBlock),
                    partiallyOpen: true,
                  })
                  setFocus({
                    type: 'block:document-title',
                    blockId: idToSourceBlockId(targetBlock),
                    temporaryBackLink: {
                      title: activeDocument?.title || 'Untitled',
                      id: idToSourceBlockId(activeDocument ?? block),
                      edgeId: idToSourceBlockId(
                        parentBlock && isRichTextBlock(parentBlock)
                          ? parentBlock
                          : block
                      ),
                    },
                  })
                }}
              >
                <BlockIcon block={targetBlock} />
                <span className="max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap">
                  {targetTitle}
                </span>
              </span>
            )}
            {isTargetUnresolved && (
              <span className="text-xs text-red pl-1.5 pr-2 py-0.5 flex items-center space-x-1 rounded gap-1">
                <AlertIcon className="size-4" />
                Unresolved component
              </span>
            )}
            <span
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setExpanded((z) => !z)
              }}
              className="flex items-center justify-center w-6 h-6 -ml-1 hover:bg-dark-elevated rounded-md"
            >
              {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </span>
          </div>
        </div>
      </NestedDocumentBlockLinkVanilla>

      {/* {allowOptions && (
        <div
          className={clsx(
            'relative z-50 flex space-x-3 transition-[opacity,visibility]',
            !isSelected &&
              'invisible opacity-0 group-focus-within/link-wrapper:visible group-focus-within/link-wrapper:opacity-100 group-hover/wrapper:visible group-hover/wrapper:opacity-100'
          )}
        >
          <BlockLabelDropdownMenu
            block={block}
            path={path}
            parentBlock={parentBlock}
          />
        </div>
      )} */}

      {expanded && (
        <ChildBlocksExpanded
          block={block}
          parentBlock={parentBlock}
          pathForNestedBlocks={pathForNestedBlocks}
        />
      )}
    </div>
  )

  if (expanded) return node
  else {
    return (
      <BlockPreview
        block={block}
        className={clsx('translate-x-2', hidePreview && 'hidden')}
      >
        {node}
      </BlockPreview>
    )
  }
}

export const ChildBlocksExpanded = ({
  block,
  parentBlock,
  pathForNestedBlocks,
}: {
  block: ReferenceBlock | DocumentBlock | ComponentBlock | InstanceBlock
  parentBlock: Block | null
  pathForNestedBlocks: string
}) => {
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const node = getNode?.(block.id)
  const isDirectChild = node?.parentKey === parentBlock?.id

  return (
    <div className="w-full">
      {parentBlock && isReferenceBlock(parentBlock) && !isDirectChild && (
        <div className="py-2 px-4 bg-warning/10 text-warning text-xs">
          You are editing a block that's being used elsewhere. Make sure you do
          so intentionally.
        </div>
      )}
      <div className="pl-4 w-full relative pr-4 pb-2 pt-2">
        <Blocks block={block} path={pathForNestedBlocks} variant="nested" />
      </div>
    </div>
  )
}

const RenamingInput = ({
  block,
  path,
}: {
  block: Block
  parent: Block
  path: string
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const setIsRenamingBlockId = useEditorStore((s) => s.setIsRenamingBlockId)
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)
  const getSelectableElement = useBlockWrapperStore(
    (s) => s.getSelectableElement
  )
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)

  const {
    onChange,
    sync: _,
    valueRef: __,
    ...syncedInputProps
  } = useSyncedValue<HTMLInputElement>({
    value: block.title,
    customEventsKey: `synced-value:document-nested:renaming:${idToSourceBlockId(
      block
    )}`,
    setValue: React.useCallback(
      async (v, { internalValueRef, customEventsKey }) => {
        if (!replicache?.mutate) return
        const getParams = (value: string): UpdateBlockParams => ({
          path,
          id: block.id,
          instanceChildTarget: block.instanceChildTarget,
          data: {
            type: block.type,
            title: value,
          },
          strategy: 'merge',
          isoNow: new Date().toISOString(),
        })
        const { undo } = await replicache.mutate.updateBlock(getParams(v))
        if (undo) {
          undoManager.current?.push({
            redo: [
              {
                op: 'focus',
                args: {
                  focus: {
                    type: 'block:document-title',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                  preferredTabKey: activeTabKey,
                },
              },
              { op: 'updateBlock', args: getParams(internalValueRef.current) },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
            undo: [
              {
                op: 'focus',
                args: {
                  focus: {
                    type: 'block:document-title',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                  preferredTabKey: activeTabKey,
                },
              },
              { op: 'blockBatchMutations', args: { mutations: undo } },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
          })
        }
      },
      [
        replicache?.mutate,
        path,
        block.id,
        block.instanceChildTarget,
        block.type,
        block.sourceBlockId,
        undoManager,
        activeTabKey,
      ]
    ),
  })

  return (
    <input
      className="relative z-30 w-[75%] border-transparent ring-transparent px-1 py-0.5 rounded-sm text-sm font-medium bg-transparent border-none ring-0 outline-none focus:ring-0 focus:outline-none focus:border-transparent focus:ring-transparent focus-visible:ring-0 focus-visible:outline-none focus-visible:border-transparent focus-visible:ring-transparent"
      autoFocus
      onBlur={() => {
        setIsRenamingBlockId(null)
      }}
      onFocus={(e) => {
        e.currentTarget.select()
        setSelectedBlockId(idToSourceBlockId(block))
      }}
      onPointerDown={(e) => {
        e.stopPropagation()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Escape') {
          e.preventDefault()
          const selectableElement = getSelectableElement()
          if (selectableElement) selectableElement.focus()
          else e.currentTarget.blur()
        }
      }}
      type="text"
      onChange={(e) => onChange(e.target.value)}
      {...syncedInputProps}
    />
  )
}

type NestedDocumentBlockLinkVanillaProps = Pick<
  NestedDocumentBlockLinkProps,
  'block' | 'className' | 'children' | 'disabled' | 'prefix'
> &
  Omit<LinkProps, 'href'> & {
    onFocus?: () => void
    expanded: boolean
  } & { parentBlock: Block | null }

const NestedDocumentBlockLinkVanilla = ({
  block,
  parentBlock,
  className,
  prefix,
  children,
  disabled,
  expanded: _expanded,
  ...rest
}: NestedDocumentBlockLinkVanillaProps) => {
  const addTab = useTabsStore((s) => s.addTab)
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const setFocus = useRepoStore((s) => s.setFocus)

  const content = (
    <span
      className={clsx(
        'absolute top-0 inline-flex items-center rounded-[5px] w-full h-full gap-2 left-0 pl-3 text-dark-bright cursor-default',
        block.hidden && 'opacity-30'
      )}
    >
      <span className="shrink-0">
        <BlockIcon block={block} parent={parentBlock ?? undefined} />
      </span>
      {children || <span>{block.title || 'Untitled'}</span>}
    </span>
  )

  if (disabled) {
    return (
      <span
        key={block.id}
        data-block-id={idToSourceBlockId(block)}
        className={clsx(
          'flex-1 cursor-default px-3 focus:outline-none pointer-events-auto',
          className
        )}
        {...rest}
      >
        {content}
      </span>
    )
  }

  return (
    <ButtonLink
      href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${
        prefix || ''
      }${idToSourceBlockId(block)}`}
      target="_self"
      unstyled
      focusStyled
      key={block.id}
      data-block-id={idToSourceBlockId(block)}
      className={clsx(
        'flex-1 cursor-default px-3 focus:outline-none pointer-events-auto bg-dark-shade hover:bg-dark-shade-hover',
        className
      )}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey) return
        addTab({
          key: (prefix || '') + idToSourceBlockId(block),
          partiallyOpen: true,
        })
        setFocus({
          type: 'block:document-title',
          blockId: idToSourceBlockId(block),
        })
      }}
      {...rest}
    >
      {content}
    </ButtonLink>
  )
}

export const getExpandedStateKey = (
  block: AtLeast<Block, 'id' | 'sourceBlockId'>
) => `nested-link:${idToSourceBlockId(block)}:expanded`

export const expandedStateStorage = 'localStorage' as const

export const useExpandedState = (block: Block | undefined) => {
  const [expanded, setExpanded] = useStorage(
    expandedStateStorage,
    (z) => z.boolean(),
    block ? getExpandedStateKey(block) : '__empty_expanded_state_key__',
    {
      initialValue: false,
      enabled: !!block,
      dangerouslyCalculateOnHydration: true,
    }
  )

  const queryClient = useQueryClient()

  const setMultiple = React.useCallback(
    (blocks: Block[], value: boolean) => {
      if (!blocks.length) return
      blocks.forEach((b) => {
        const key = getExpandedStateKey(b)
        window.localStorage.setItem(key, JSON.stringify(value))
        queryClient.refetchQueries({ queryKey: [key] })
      })
    },
    [queryClient]
  )

  return { expanded, setExpanded, setMultiple }
}
